// Force re-sync
import axios from 'axios';
import { handleError } from './utils/errorHandler'; // Import handleError
import useAuthStore from './store/useAuthStore'; // Import useAuthStore
import { Capacitor } from '@capacitor/core';

// --- Main Backend API Client ---

const getBaseURL = () => {
  // console.log("🔍 [BASE_URL] Detectando entorno...");
  // console.log("📍 Hostname:", window.location.hostname);
  // console.log("📱 UserAgent:", navigator.userAgent);
  // console.log("🏗️ Capacitor Native:", Capacitor.isNativePlatform());

  // 1. Prioridad Absoluta: Variable de entorno
  if (process.env.REACT_APP_API_URL) {
      // console.log("🌐 Usando API URL desde ENV:", process.env.REACT_APP_API_URL);
      return process.env.REACT_APP_API_URL;
  }

  // 2. Detección de dispositivos móviles (Capacitor o Navegador Móvil)
  const isMobile = Capacitor.isNativePlatform() || 
                   /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  if (isMobile) {
    // console.log("🚀 [ENTORNO MÓVIL] Forzando uso de Render (Producción)");
    return 'https://flujo-app-backend.onrender.com';
  }

  // 3. Solo si estamos en un navegador DESKTOP y es localhost, usamos el backend local
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    // console.log("💻 [ENTORNO DESKTOP] Usando localhost:8000");
    return 'http://localhost:8000';
  }

  // 4. Fallback final para producción web o cualquier otro caso: Usar la URL de Render Real
  // console.log("🌍 [FALLBACK] Usando Render (Producción)");
  return 'https://flujo-app-backend.onrender.com';
};

const apiClient = axios.create({
  baseURL: getBaseURL(),
});

apiClient.interceptors.request.use(config => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // console.log(`📡 [API_REQ] ${config.method.toUpperCase()} ${config.url}`);
  return config;
});

apiClient.interceptors.response.use(
  response => {
    // console.log(`✅ [API_RES] ${response.config.url} SUCCESS`);
    return response;
  },
  error => {
    console.error(`❌ [API_ERR] ${error.config?.url} ERROR:`, error.response?.status, error.message);
    // SILENCIAR errores de cancelación (típicos de micro-cortes o navegación rápida)
    if (axios.isCancel(error)) {
        return Promise.reject(error);
    }

    if (error.response && error.response.status === 401) {
      // CRÍTICO: NO disparar logout si el error viene del proceso de login mismo.
      // Esto evita el bug de "Logging in → vuelve a Login" en Android/Capacitor.
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/token') || url.includes('/users/me');
      
      if (!isAuthEndpoint) {
        console.warn("⚠️ Error 401 detectado. Redirigiendo a login...");
        if (window.location.pathname !== '/login') {
          useAuthStore.getState().logout().then(() => {
            window.location.href = '/login';
          });
        }
      }
    }
    return Promise.reject(error);
  }
);

// --- Commissions Microservice API Client ---

const getCommissionsBaseURL = () => {
  if (process.env.NODE_ENV === 'production') {
    // TODO: Update with the production URL for the commissions backend
    return 'https://comisiones-backend-8czo.onrender.com'; // Example URL
  }
  return 'http://localhost:8008';
};

const commissionsApiClient = axios.create({
  baseURL: getCommissionsBaseURL(),
});

// You might not need an interceptor for the commissions service if it's not protected by the same auth

// --- API Functions ---

export const login = async (email, password) => {
  try {
    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);
    
    // 1. Obtener Token
    const response = await apiClient.post('/token', params);
    const token = response.data.access_token;
    
    // 2. Obtener datos del usuario usando el token recién obtenido
    // No usamos el interceptor aquí porque el store aún no tiene el token
    const userResponse = await apiClient.get('/users/me', {
      headers: { Authorization: `Bearer ${token}` }
    });
    const user = userResponse.data;
    
    // 3. Solo cuando tenemos AMBOS (token y user), actualizamos el estado global
    // Esto evita que RoleBasedRedirect se confunda con un user: null
    useAuthStore.getState().login(token, user);
    
    return token;
  } catch (error) {
    // Limpiar cualquier residuo de sesión si algo falla
    useAuthStore.getState().logout();
    handleError(error, "Error al iniciar sesión.");
    throw error;
  }
};

export const logout = () => {
  useAuthStore.getState().logout();
};

export const register = async (email, password) => {
  try {
    const response = await apiClient.post('/users/', { email, password });
    return response.data;
  } catch (error) {
    handleError(error, "Error al registrar la cuenta.");
    throw error;
  }
};

export const getTransactions = async (params = {}) => {
  try {
    const response = await apiClient.get('/transactions/', { params });
    return response.data;
  } catch (error) {
    handleError(error, "Error al cargar las transacciones.");
    throw error;
  }
};

export const createTransaction = async (transaction) => {
  try {
    const response = await apiClient.post('/transactions/', transaction);
    return response.data;
  } catch (error) {
    handleError(error, "Error al crear la transacción.");
    throw error;
  }
};

export const updateTransaction = async (id, transaction) => {
  try {
    const response = await apiClient.put(`/transactions/${id}`, transaction);
    return response.data;
  } catch (error) {
    handleError(error, "Error al actualizar la transacción.");
    throw error;
  }
};

export const updateTransactionsBulk = async (transactionIds, status = null, category = null, subcategory = null) => {
  try {
    const payload = {
      transaction_ids: transactionIds,
      status: status,
      category: category,
      subcategory: subcategory // Nuevo campo
    };
    const response = await apiClient.put('/transactions/bulk-update', payload);
    return response.data;
  } catch (error) {
    handleError(error, "Error al actualizar las transacciones masivamente.");
    throw error;
  }
};

export const checkInvoiceImpact = async (transactionIds) => {
  try {
    const response = await apiClient.post('/transactions/check-invoice-impact', {
      transaction_ids: transactionIds
    });
    return response.data;
  } catch (error) {
    handleError(error, "Error al consultar impacto en facturación.");
    throw error;
  }
};

export const deleteTransaction = async (id) => {
  try {
    const response = await apiClient.delete(`/transactions/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, "Error al eliminar la transacción.");
    throw error;
  }
};

export const deleteAllTransactions = async () => {
  try {
    const response = await apiClient.delete('/transactions/all');
    return response.data;
  } catch (error) {
    handleError(error, "Error al eliminar todas las transacciones.");
    throw error;
  }
};

export const markAllAsCompleted = async () => {
  try {
    const response = await apiClient.post('/transactions/mark-all-as-completed');
    return response.data;
  } catch (error) {
    handleError(error, 'Error al marcar las transacciones como completadas.');
    throw error;
  }
};

export const getLogsByTransactionId = async (id) => {
  try {
    const response = await apiClient.get(`/logs/transaction/${id}`);
    return response.data;
  } catch (error) {
    handleError(error, "Error al cargar el historial de la transacción.");
    throw error;
  }
};

export const getKpiSettings = async () => {
  try {
    const response = await apiClient.get('/kpi-settings/');
    return response.data;
  } catch (error) {
    handleError(error, "Error al cargar la configuración de KPI.");
    throw error;
  }
};

export const updateKpiSettings = async (settings) => {
  try {
    const response = await apiClient.post('/kpi-settings/', settings);
    return response.data;
  } catch (error) {
    handleError(error, "Error al actualizar la configuración de KPI.");
    throw error;
  }
};

export const exportTransactions = async (startDate, endDate) => {
  try {
    const response = await apiClient.get('/export_transactions/', {
      params: { start_date: startDate, end_date: endDate },
      responseType: 'blob', // Important for file downloads
    });
    return response.data;
  } catch (error) {
    handleError(error, "Error al exportar las transacciones.");
    throw error;
  }
};

// --- Commissions API Functions ---

export const processInitialCommissions = async (voucherFile, conductoresFile) => {
  const formData = new FormData();
  formData.append("voucher_file", voucherFile);
  formData.append("conductores_file", conductoresFile);

  try {
    const response = await commissionsApiClient.post('/procesar-inicial/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error al procesar los archivos de comisiones.');
    throw error;
  }
};


export const generateFinalReports = async (finalData) => {
  try {
    const response = await commissionsApiClient.post('/generar-reportes-finales/', finalData, {
      responseType: 'blob', // Important for file downloads
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error al generar los reportes finales.');
    throw error;
  }
};

// --- Google Sheets Sync & Reports API Functions ---
export const syncGoogleSheets = async () => {
  try {
    const response = await apiClient.post('/sync/sheets');
    return response.data;
  } catch (error) {
    handleError(error, 'Error al sincronizar con Google Sheets.');
    throw error;
  }
}

export const getIncomeStatementData = async (year, segmentacion = null) => {
  try {
    let url = `/reports/income-statement-data?year=${year}`;
    if (segmentacion) {
      url += `&segmentacion=${encodeURIComponent(segmentacion)}`;
    }
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    handleError(error, 'Error al cargar el Estado de Resultados.');
    throw error;
  }
}

export const getAvailableSegments = async () => {
  try {
    const response = await apiClient.get('/reports/available-segments');
    return response.data;
  } catch (error) {
    console.error("Error fetching segments:", error);
    return [];
  }
}

// --- COST ALLOCATION API FUNCTIONS ---

export const getCostAllocations = async (year, month) => {
  try {
    const response = await apiClient.get(`/cost-allocations/?year=${year}&month=${month}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching cost allocations:", error);
    return [];
  }
};

export const saveCostAllocations = async (allocations) => {
  try {
    const response = await apiClient.post('/cost-allocations/bulk', allocations);
    return response.data;
  } catch (error) {
    handleError(error, 'Error al guardar las proporciones.');
    throw error;
  }
};

export const analyzePortfolio = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const response = await apiClient.post('/api/portfolio/analyze', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error al analizar el archivo de cartera.');
    throw error;
  }
};

// --- GESTIÓN DE PAGOS A CONDUCTORES ---

export const previewDriverPayments = async (preliqFile, flotaFile, contadorFile) => {
  const formData = new FormData();
  formData.append("preliq_file", preliqFile);
  formData.append("flota_file", flotaFile);
  formData.append("contador_file", contadorFile);
  try {
    const response = await apiClient.post('/api/driver-payments/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error al procesar los archivos de liquidación.');
    throw error;
  }
};

export const confirmDriverPayments = async (payments) => {
  try {
    const response = await apiClient.post('/api/driver-payments/confirm', payments);
    return response.data;
  } catch (error) {
    handleError(error, 'Error al confirmar las transacciones de pago.');
    throw error;
  }
};

// --- DRIVER ONBOARDING & DOCUMENT MANAGEMENT ---

export const registerDriver = async (driverData) => {
  try {
    const response = await apiClient.post('/auth/register-driver', driverData);
    return response.data;
  } catch (error) {
    handleError(error, 'Error al registrar al conductor.');
    throw error;
  }
};

export const getDriverStatus = async () => {
  try {
    const response = await apiClient.get('/transport/drivers/me/status');
    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener estado de aprobación.');
    throw error;
  }
};

export const getDriverDocuments = async () => {
  try {
    const response = await apiClient.get('/transport/drivers/me/documents');
    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener documentos del conductor.');
    throw error;
  }
};

export const uploadDriverDocument = async (documentType, file, expirationDate) => {
  const formData = new FormData();
  formData.append('document_type', documentType);
  formData.append('file', file);
  if (expirationDate) {
    formData.append('expiration_date', expirationDate);
  }
  try {
    const response = await apiClient.post('/transport/drivers/me/documents', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error al cargar el documento.');
    throw error;
  }
};

export const getPendingDocuments = async () => {
  try {
    const response = await apiClient.get('/transport/admin/documents/pending');
    return response.data;
  } catch (error) {
    handleError(error, 'Error al obtener documentos pendientes de aprobación.');
    throw error;
  }
};

export const reviewDocument = async (docId, status, rejectionReason = '', expirationDate = '') => {
  try {
    const response = await apiClient.post(`/transport/admin/documents/${docId}/review`, {
      status,
      rejection_reason: rejectionReason,
      expiration_date: expirationDate,
    });
    return response.data;
  } catch (error) {
    handleError(error, 'Error al procesar la revisión del documento.');
    throw error;
  }
};

export const forgotPassword = async (email) => {
  try {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    handleError(error, 'Error al solicitar el enlace de recuperación.');
    throw error;
  }
};

export const resetPassword = async (token, newPassword) => {
  try {
    const response = await apiClient.post('/auth/reset-password', { token, new_password: newPassword });
    return response.data;
  } catch (error) {
    handleError(error, 'Error al restablecer la contraseña.');
    throw error;
  }
};

export default apiClient;